import { Button, Popconfirm, Space, Tooltip, Typography } from 'antd'
import {
  DeleteOutlined,
  ExportOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import { useWorkflowRecordFiles } from './hooks/useWorkflowRecordFiles'
import DriveItemTree from './components/DriveItemTree'
import FileUploader from './components/FileUploader'
import type { WorkflowRecordFileManagerProps } from './types'
import styles from './index.module.css'

/**
 * Uploads, browses, and removes files in a workflow record's SharePoint folder.
 *
 * Operations take effect immediately. The consumer supplies the record and its
 * verified folder metadata and owns the surrounding lifecycle.
 */
function WorkflowRecordFileManager(props: WorkflowRecordFileManagerProps) {
  const controller = useWorkflowRecordFiles(props)
  const filesLoading =
    controller.refreshing || !controller.branches[props.metadata.root.id]
  const removeDisabled =
    filesLoading ||
    controller.busy ||
    controller.denied ||
    controller.selected.length === 0
  const uploadDisabledReason =
    controller.busy && !controller.removing
      ? "Can't remove files or folders while uploading"
      : undefined
  let removeDisabledReason = uploadDisabledReason
  if (
    !removeDisabledReason &&
    !filesLoading &&
    controller.selected.length === 0
  )
    removeDisabledReason = 'Select at least one file to remove'
  return (
    <div className={styles.manager}>
      <FileUploader
        entries={controller.queue}
        disabled={controller.denied || controller.removing}
        onAdd={controller.add}
        onRetry={controller.retry}
        onError={controller.report}
      />
      <section aria-label="Browse files">
        <div className={styles.toolbar}>
          <Space className={styles.fileHeading} size={0} align="baseline">
            <Typography.Title level={4}>
              Folder: {props.metadata.renderedLabel ?? props.metadata.root.name}
            </Typography.Title>
            {props.metadata.root.webUrl && (
              <Button
                type="link"
                className={styles.folderLink}
                aria-label="Open record folder in SharePoint"
                title="Open record folder in SharePoint"
                icon={<ExportOutlined />}
                href={props.metadata.root.webUrl}
                target="_blank"
                rel="noreferrer"
              />
            )}
          </Space>
          <Space className={styles.fileActions}>
            <Tooltip title={removeDisabledReason}>
              <span>
                <Popconfirm
                  title={
                    <div className={styles.removeConfirmation}>
                      <Typography.Text strong>
                        Are you sure you want to remove{' '}
                        {controller.selected.length} selected items?
                      </Typography.Text>
                      <div>
                        Selected items, including all contents of selected
                        folders, will be removed from SharePoint and moved to
                        its recycle bin.
                      </div>
                    </div>
                  }
                  placement="topRight"
                  disabled={removeDisabled}
                  okText="OK"
                  onConfirm={() => controller.remove(controller.selected)}
                >
                  <Button
                    aria-label="Remove"
                    className={styles.removeButton}
                    icon={<DeleteOutlined />}
                    danger
                    disabled={removeDisabled}
                    loading={controller.removing}
                  >
                    Remove
                  </Button>
                </Popconfirm>
              </span>
            </Tooltip>
            <Tooltip title={uploadDisabledReason}>
              <span>
                <Button
                  aria-label="Refresh"
                  title={
                    filesLoading || uploadDisabledReason ? undefined : 'Refresh'
                  }
                  icon={<ReloadOutlined />}
                  disabled={controller.busy || controller.refreshing}
                  onClick={() => {
                    controller.setSelected([])
                    void controller.refresh()
                  }}
                />
              </span>
            </Tooltip>
          </Space>
        </div>
        <DriveItemTree
          rootId={props.metadata.root.id}
          branches={controller.branches}
          refreshing={controller.refreshing}
          selected={controller.selected}
          disabled={controller.busy || controller.denied}
          disabledReason={uploadDisabledReason}
          onSelect={controller.setSelected}
          onLoad={controller.load}
        />
      </section>
    </div>
  )
}
export default WorkflowRecordFileManager
